const SCORE = {
  startingWithSubstring: 3,
  emptyPattern: 3,
  substring: 2,
  fuzzyMatch: 1,
  noMatch: 0,
};

function findPosOf(
  substring: string,
  str: string,
  strTrim: number = 0,
): [number, number] | undefined {
  const startIndex = str.indexOf(substring);
  if (startIndex >= 0) {
    return [startIndex + strTrim, startIndex + substring.length + strTrim];
  }
  return undefined;
}

function normalizeString(str: string) {
  let normalizedStr = str.trimStart();
  const trimStart = str.length - normalizedStr.length;
  normalizedStr = normalizedStr.trimEnd().toLowerCase();
  return [normalizedStr, trimStart] as const;
}

interface MatchResult {
  match: boolean;
  pos?: [number, number];
  score: number;
}

function findSubstringMatch(pattern: string, str: string): MatchResult {
  if (pattern.length === 0) {
    // empty pattern should always match and score highest
    return { match: true, pos: undefined, score: SCORE.emptyPattern };
  }
  const [normalizedPattern] = normalizeString(pattern);
  const [normalizedStr, strTrim] = normalizeString(str);

  const pos = findPosOf(normalizedPattern, normalizedStr, strTrim);
  if (pos !== undefined) {
    // score higher when position starts at the beginning of trimmed string
    const score =
      pos[0] === strTrim ? SCORE.startingWithSubstring : SCORE.substring;
    return {
      match: true,
      pos,
      score,
    };
  }
  return { match: false, score: 0 };
}

// https://stackoverflow.com/questions/3446170/escape-string-for-use-in-javascript-regex/6969486#6969486
// escape any special characters by adding a backslash in front
function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

function findFuzzyMatch(pattern: string, str: string): MatchResult {
  if (pattern.length === 0) {
    // empty pattern should always match and score highest
    return { match: true, pos: undefined, score: SCORE.emptyPattern };
  }

  const [normalizedPattern] = normalizeString(pattern);
  const [normalizedStr, strTrim] = normalizeString(str);

  const regexString =
    '.*(' +
    normalizedPattern
      .split('')
      .map((part) => escapeRegExp(part))
      .join('.*?') +
    ').*';
  const regex = new RegExp(regexString);
  const result = regex.exec(normalizedStr);

  if (result !== null) {
    return {
      match: true,
      pos: findPosOf(result[1], normalizedStr, strTrim),
      score: SCORE.fuzzyMatch,
    };
  }

  return {
    match: false,
    score: SCORE.noMatch,
  };
}

export function fuzzyMatch(pattern: string, str: string): MatchResult {
  const substringMatch = findSubstringMatch(pattern, str);
  if (substringMatch.match) {
    return substringMatch;
  }

  return findFuzzyMatch(pattern, str);
}

const maxLabelLength = 53; // Maximum amount of characters in the autocomplete label
const middleEllipsisStartPos = 26; // Characters before the middle ellipsis
const middleEllipsisLength = 1; // Length of the middle ellipsis (…)

// Characters after the middle ellipsis
const afterMiddleEllipsisLength =
  maxLabelLength - middleEllipsisStartPos - middleEllipsisLength;

function getMiddleEllipsisEndPos(label: string) {
  return label.length - afterMiddleEllipsisLength;
}

export function labelShouldBeTruncated(label: string): boolean {
  return label.length > maxLabelLength;
}

/* Will add middle ellipsis (…) if the label is longer than maxLabelLength characters */
export function convertToMiddleEllipsis(label: string): string {
  return `${label.slice(0, middleEllipsisStartPos)}…${label.slice(
    getMiddleEllipsisEndPos(label),
  )}`;
}

function convertPosToMiddleEllipsis(
  position: number,
  label: string,
  defaultValue: number,
): number {
  if (position <= middleEllipsisStartPos) {
    return position;
  }

  const endPos = getMiddleEllipsisEndPos(label);

  if (position > endPos) {
    return middleEllipsisStartPos + middleEllipsisLength + (position - endPos);
  }

  return defaultValue;
}

export function getFuzzyMatchFullLabelPosition(
  pattern: string,
  fullLabel: string | undefined,
) {
  if (fullLabel === undefined) {
    return [];
  }
  const isTruncated = labelShouldBeTruncated(fullLabel);

  const pos = fuzzyMatch(pattern, fullLabel).pos;

  if (pos === undefined) {
    return undefined;
  }

  if (!isTruncated) {
    return pos;
  }

  return [
    convertPosToMiddleEllipsis(pos[0], fullLabel, middleEllipsisStartPos),
    convertPosToMiddleEllipsis(
      pos[1],
      fullLabel,
      middleEllipsisStartPos + middleEllipsisLength,
    ),
  ];
}
